// Rates, Ratios and Variations Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const ratesRatiosVariationsSamples = {
  fundamental: [
    {
      id: 'rates_ratios_fund_01',
      problem: "If 3 apples cost $6, how much do 5 apples cost?",
      problemType: "rates_ratios_variations",
      difficulty: "fundamental",
      description: "Basic ratio problem",
      graphData: {
        nodes: [
          {
            id: "given_ratio",
            name: "Given Ratio",
            type: "concepts",
            description: "The known ratio",
            value: "3 apples : $6",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "unit_rate_formula",
            name: "Unit Rate Formula",
            type: "formulas",
            description: "Unit Rate = Total Cost ÷ Number of Items",
            value: "Unit Rate = Total Cost ÷ Number of Items",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "target_quantity",
            name: "Target Quantity",
            type: "concepts",
            description: "The quantity we want to find",
            value: "5 apples",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_identify_ratio",
            name: "Step 1: Identify Ratio",
            type: "steps",
            description: "Identify the given ratio",
            value: "Step 1: Identify the given ratio of 3 apples to $6",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_calculate_unit_rate",
            name: "Step 2: Calculate Unit Rate",
            type: "steps",
            description: "Find cost per single item",
            value: "Step 2: Calculate unit rate = $6 ÷ 3 = $2 per apple",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_multiply_by_target",
            name: "Step 3: Multiply by Target",
            type: "steps",
            description: "Multiply unit rate by target quantity",
            value: "Step 3: Multiply unit rate by target quantity = $2 × 5 = $10",
            importance: 10,
            x: 100, y: 0, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The cost of 5 apples",
            value: "$10",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "given_ratio", to: "unit_rate_formula", type: "gives", strength: 10 },
          { from: "unit_rate_formula", to: "step1_identify_ratio", type: "used in", strength: 9 },
          { from: "given_ratio", to: "step1_identify_ratio", type: "used in", strength: 9 },
          { from: "unit_rate_formula", to: "step2_calculate_unit_rate", type: "used in", strength: 9 },
          { from: "step2_calculate_unit_rate", to: "step3_multiply_by_target", type: "leads to", strength: 10 },
          { from: "target_quantity", to: "step3_multiply_by_target", type: "used in", strength: 9 },
          { from: "step3_multiply_by_target", to: "solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'rates_ratios_fund_02',
      problem: "A car travels 120 km in 2 hours. What is its speed in km/h?",
      problemType: "rates_ratios_variations",
      difficulty: "fundamental",
      description: "Basic rate calculation",
      graphData: {
        nodes: [
          {
            id: "distance",
            name: "Distance",
            type: "concepts",
            description: "The distance traveled",
            value: "120 km",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "time",
            name: "Time",
            type: "concepts",
            description: "The time taken",
            value: "2 hours",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "speed_formula",
            name: "Speed Formula",
            type: "formulas",
            description: "Speed = Distance ÷ Time",
            value: "Speed = Distance ÷ Time",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step1_identify_values",
            name: "Step 1: Identify Values",
            type: "steps",
            description: "Identify distance and time",
            value: "Step 1: Identify distance = 120 km, time = 2 hours",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Apply speed formula",
            value: "Step 2: Apply speed formula = 120 ÷ 2 = 60 km/h",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_write_answer",
            name: "Step 3: Write Answer",
            type: "steps",
            description: "Write the final answer",
            value: "Step 3: Write answer = 60 km/h",
            importance: 9,
            x: 100, y: 50, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The speed of the car",
            value: "60 km/h",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "distance", to: "speed_formula", type: "used in", strength: 9 },
          { from: "time", to: "speed_formula", type: "used in", strength: 9 },
          { from: "distance", to: "step1_identify_values", type: "used in", strength: 9 },
          { from: "time", to: "step1_identify_values", type: "used in", strength: 9 },
          { from: "speed_formula", to: "step2_apply_formula", type: "used in", strength: 10 },
          { from: "step1_identify_values", to: "step2_apply_formula", type: "leads to", strength: 10 },
          { from: "step2_apply_formula", to: "step3_write_answer", type: "leads to", strength: 10 },
          { from: "step3_write_answer", to: "solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'rates_ratios_fund_03',
      problem: "If 4 workers can complete a job in 6 days, how many days will 6 workers take?",
      problemType: "rates_ratios_variations",
      difficulty: "fundamental",
      description: "Inverse proportion problem",
      graphData: {
        nodes: [
          {
            id: "given_situation",
            name: "Given Situation",
            type: "concepts",
            description: "The known situation",
            value: "4 workers : 6 days",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "inverse_proportion_formula",
            name: "Inverse Proportion Formula",
            type: "formulas",
            description: "More workers = fewer days",
            value: "Days = (Original Workers × Original Days) ÷ New Workers",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "new_workers",
            name: "New Workers",
            type: "concepts",
            description: "The new number of workers",
            value: "6 workers",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_identify_values",
            name: "Step 1: Identify Values",
            type: "steps",
            description: "Identify original and new workers",
            value: "Step 1: Original workers = 4, original days = 6, new workers = 6",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Apply inverse proportion formula",
            value: "Step 2: Apply formula = (4×6) ÷ 6 = 4 days",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_write_answer",
            name: "Step 3: Write Answer",
            type: "steps",
            description: "Write the final answer",
            value: "Step 3: Write answer = 4 days",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The time for 6 workers",
            value: "4 days",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "given_situation", to: "inverse_proportion_formula", type: "used with", strength: 10 },
          { from: "new_workers", to: "inverse_proportion_formula", type: "used in", strength: 9 },
          { from: "given_situation", to: "step1_identify_values", type: "used in", strength: 9 },
          { from: "new_workers", to: "step1_identify_values", type: "used in", strength: 9 },
          { from: "inverse_proportion_formula", to: "step2_apply_formula", type: "used in", strength: 10 },
          { from: "step1_identify_values", to: "step2_apply_formula", type: "leads to", strength: 10 },
          { from: "step2_apply_formula", to: "step3_write_answer", type: "leads to", strength: 10 },
          { from: "step3_write_answer", to: "solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'rates_ratios_fund_04',
      problem: "A recipe calls for 2 cups of flour for every 3 cups of sugar. How much flour is needed for 9 cups of sugar?",
      problemType: "rates_ratios_variations",
      difficulty: "fundamental",
      description: "Direct proportion problem",
      graphData: {
        nodes: [
          {
            id: "given_ratio",
            name: "Given Ratio",
            type: "concepts",
            description: "The known ratio",
            value: "2 cups flour : 3 cups sugar",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "direct_proportion_formula",
            name: "Direct Proportion Formula",
            type: "formulas",
            description: "Direct proportion relationship",
            value: "Flour needed = (Given flour × Target sugar) ÷ Given sugar",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "target_sugar",
            name: "Target Sugar",
            type: "concepts",
            description: "The amount of sugar we have",
            value: "9 cups sugar",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_identify_ratio",
            name: "Step 1: Identify Ratio",
            type: "steps",
            description: "Identify the given ratio",
            value: "Step 1: Identify ratio = 2 cups flour : 3 cups sugar",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Apply direct proportion formula",
            value: "Step 2: Apply formula = (2 × 9) ÷ 3 = 6 cups flour",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_write_answer",
            name: "Step 3: Write Answer",
            type: "steps",
            description: "Write the final answer",
            value: "Step 3: Write answer = 6 cups flour",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The amount of flour needed",
            value: "6 cups flour",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "given_ratio", to: "direct_proportion_formula", type: "used with", strength: 10 },
          { from: "target_sugar", to: "direct_proportion_formula", type: "used in", strength: 9 },
          { from: "given_ratio", to: "step1_identify_ratio", type: "used in", strength: 9 },
          { from: "target_sugar", to: "step1_identify_ratio", type: "used in", strength: 9 },
          { from: "direct_proportion_formula", to: "step2_apply_formula", type: "used in", strength: 10 },
          { from: "step1_identify_ratio", to: "step2_apply_formula", type: "leads to", strength: 10 },
          { from: "step2_apply_formula", to: "step3_write_answer", type: "leads to", strength: 10 },
          { from: "step3_write_answer", to: "solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'rates_ratios_fund_05',
      problem: "A pump can fill a tank in 8 hours. How much of the tank will be filled in 3 hours?",
      problemType: "rates_ratios_variations",
      difficulty: "fundamental",
      description: "Rate of work problem",
      graphData: {
        nodes: [
          {
            id: "total_time",
            name: "Total Time",
            type: "concepts",
            description: "Time to fill entire tank",
            value: "8 hours",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "work_rate_formula",
            name: "Work Rate Formula",
            type: "formulas",
            description: "Work Rate = 1 ÷ Total Time",
            value: "Work Rate = 1 ÷ Total Time",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "partial_time",
            name: "Partial Time",
            type: "concepts",
            description: "The time we want to find",
            value: "3 hours",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_calculate_rate",
            name: "Step 1: Calculate Rate",
            type: "steps",
            description: "Calculate work rate per hour",
            value: "Step 1: Work rate = 1 ÷ 8 = 1/8 of tank per hour",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_multiply_by_time",
            name: "Step 2: Multiply by Time",
            type: "steps",
            description: "Multiply rate by partial time",
            value: "Step 2: Fraction filled = (1/8) × 3 = 3/8 of tank",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_write_answer",
            name: "Step 3: Write Answer",
            type: "steps",
            description: "Write the final answer",
            value: "Step 3: Write answer = 3/8 of tank",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Fraction of tank filled",
            value: "3/8 of tank",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "total_time", to: "work_rate_formula", type: "used in", strength: 10 },
          { from: "partial_time", to: "work_rate_formula", type: "used with", strength: 9 },
          { from: "total_time", to: "step1_calculate_rate", type: "used in", strength: 9 },
          { from: "work_rate_formula", to: "step1_calculate_rate", type: "used in", strength: 10 },
          { from: "step1_calculate_rate", to: "step2_multiply_by_time", type: "leads to", strength: 10 },
          { from: "partial_time", to: "step2_multiply_by_time", type: "used in", strength: 9 },
          { from: "step2_multiply_by_time", to: "step3_write_answer", type: "leads to", strength: 10 },
          { from: "step3_write_answer", to: "solution", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'rates_ratios_inter_01',
      problem: "Two pipes can fill a tank in 6 hours and 8 hours respectively. How long will it take if both pipes are used together?",
      problemType: "rates_ratios_variations",
      difficulty: "intermediate",
      description: "Combined work rates",
      graphData: {
        nodes: [
          {
            id: "pipe1_rate",
            name: "Pipe 1 Rate",
            type: "concepts",
            description: "First pipe's work rate",
            value: "1/6 of tank per hour",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "pipe2_rate",
            name: "Pipe 2 Rate",
            type: "concepts",
            description: "Second pipe's work rate",
            value: "1/8 of tank per hour",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "combined_work_formula",
            name: "Combined Work Formula",
            type: "formulas",
            description: "Combined rate = Sum of individual rates",
            value: "Combined Rate = Rate₁ + Rate₂ + ... + Rateₙ",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_identify_rates",
            name: "Step 1: Identify Rates",
            type: "steps",
            description: "Identify individual work rates",
            value: "Step 1: Pipe 1 rate = 1/6, Pipe 2 rate = 1/8",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_add_rates",
            name: "Step 2: Add Rates",
            type: "steps",
            description: "Add the individual rates",
            value: "Step 2: Combined rate = 1/6 + 1/8 = 7/24 per hour",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_invert_rate",
            name: "Step 3: Invert Rate",
            type: "steps",
            description: "Invert to find total time",
            value: "Step 3: Total time = 1 ÷ (7/24) = 24/7 hours",
            importance: 10,
            x: 100, y: 0, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Time to fill tank with both pipes",
            value: "24/7 hours ≈ 3.43 hours",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "pipe1_rate", to: "combined_work_formula", type: "used in", strength: 10 },
          { from: "pipe2_rate", to: "combined_work_formula", type: "used in", strength: 10 },
          { from: "pipe1_rate", to: "step1_identify_rates", type: "used in", strength: 9 },
          { from: "pipe2_rate", to: "step1_identify_rates", type: "used in", strength: 9 },
          { from: "combined_work_formula", to: "step2_add_rates", type: "used in", strength: 10 },
          { from: "step1_identify_rates", to: "step2_add_rates", type: "leads to", strength: 10 },
          { from: "step2_add_rates", to: "step3_invert_rate", type: "leads to", strength: 10 },
          { from: "step3_invert_rate", to: "solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'rates_ratios_inter_02',
      problem: "A car travels at 60 km/h for 2 hours, then at 80 km/h for 3 hours. What is the average speed for the entire journey?",
      problemType: "rates_ratios_variations",
      difficulty: "intermediate",
      description: "Average speed calculation",
      graphData: {
        nodes: [
          {
            id: "first_leg",
            name: "First Leg",
            type: "concepts",
            description: "First part of journey",
            value: "60 km/h for 2 hours",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "second_leg",
            name: "Second Leg",
            type: "concepts",
            description: "Second part of journey",
            value: "80 km/h for 3 hours",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "average_speed_formula",
            name: "Average Speed Formula",
            type: "formulas",
            description: "Average Speed = Total Distance ÷ Total Time",
            value: "Average Speed = Total Distance ÷ Total Time",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_calculate_distances",
            name: "Step 1: Calculate Distances",
            type: "steps",
            description: "Calculate distance for each leg",
            value: "Step 1: Distance 1 = 60×2 = 120 km, Distance 2 = 80×3 = 240 km",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_add_distances",
            name: "Step 2: Add Distances",
            type: "steps",
            description: "Add total distance",
            value: "Step 2: Total distance = 120 + 240 = 360 km",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_add_times",
            name: "Step 3: Add Times",
            type: "steps",
            description: "Add total time",
            value: "Step 3: Total time = 2 + 3 = 5 hours",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step4_calculate_average",
            name: "Step 4: Calculate Average",
            type: "steps",
            description: "Apply average speed formula",
            value: "Step 4: Average speed = 360 ÷ 5 = 72 km/h",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The average speed",
            value: "72 km/h",
            importance: 10,
            x: 0, y: 150, z: -15
          }
        ],
        links: [
          { from: "first_leg", to: "step1_calculate_distances", type: "used in", strength: 9 },
          { from: "second_leg", to: "step1_calculate_distances", type: "used in", strength: 9 },
          { from: "step1_calculate_distances", to: "step2_add_distances", type: "leads to", strength: 10 },
          { from: "first_leg", to: "step3_add_times", type: "used in", strength: 9 },
          { from: "second_leg", to: "step3_add_times", type: "used in", strength: 9 },
          { from: "step2_add_distances", to: "average_speed_formula", type: "used in", strength: 10 },
          { from: "step3_add_times", to: "average_speed_formula", type: "used in", strength: 10 },
          { from: "average_speed_formula", to: "step4_calculate_average", type: "used in", strength: 10 },
          { from: "step4_calculate_average", to: "solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'rates_ratios_inter_03',
      problem: "If y varies directly with x and y = 12 when x = 3, find y when x = 7",
      problemType: "rates_ratios_variations",
      difficulty: "intermediate",
      description: "Direct variation problem",
      graphData: {
        nodes: [
          {
            id: "variation_equation",
            name: "Variation Equation",
            type: "formulas",
            description: "Direct variation formula",
            value: "y = kx",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "known_values",
            name: "Known Values",
            type: "concepts",
            description: "Given values to find k",
            value: "y = 12 when x = 3",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "target_value",
            name: "Target Value",
            type: "concepts",
            description: "The x value we want to find y for",
            value: "x = 7",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_substitute_known",
            name: "Step 1: Substitute Known",
            type: "steps",
            description: "Substitute known values to find k",
            value: "Step 1: 12 = k × 3",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_solve_for_k",
            name: "Step 2: Solve for k",
            type: "steps",
            description: "Solve for the constant k",
            value: "Step 2: k = 12 ÷ 3 = 4",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_write_equation",
            name: "Step 3: Write Equation",
            type: "steps",
            description: "Write the complete equation",
            value: "Step 3: y = 4x",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step4_substitute_target",
            name: "Step 4: Substitute Target",
            type: "steps",
            description: "Substitute target value",
            value: "Step 4: y = 4 × 7 = 28",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The value of y when x = 7",
            value: "y = 28",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "variation_equation", to: "step1_substitute_known", type: "used in", strength: 10 },
          { from: "known_values", to: "step1_substitute_known", type: "used in", strength: 9 },
          { from: "step1_substitute_known", to: "step2_solve_for_k", type: "leads to", strength: 10 },
          { from: "step2_solve_for_k", to: "step3_write_equation", type: "leads to", strength: 10 },
          { from: "variation_equation", to: "step3_write_equation", type: "used in", strength: 9 },
          { from: "step3_write_equation", to: "step4_substitute_target", type: "leads to", strength: 10 },
          { from: "target_value", to: "step4_substitute_target", type: "used in", strength: 9 },
          { from: "step4_substitute_target", to: "solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'rates_ratios_inter_04',
      problem: "If y varies inversely with x and y = 8 when x = 4, find y when x = 2",
      problemType: "rates_ratios_variations",
      difficulty: "intermediate",
      description: "Inverse variation problem",
      graphData: {
        nodes: [
          {
            id: "variation_equation",
            name: "Variation Equation",
            type: "formulas",
            description: "Inverse variation formula",
            value: "y = k/x",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "known_values",
            name: "Known Values",
            type: "concepts",
            description: "Given values to find k",
            value: "y = 8 when x = 4",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "target_value",
            name: "Target Value",
            type: "concepts",
            description: "The x value we want to find y for",
            value: "x = 2",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_substitute_known",
            name: "Step 1: Substitute Known",
            type: "steps",
            description: "Substitute known values to find k",
            value: "Step 1: 8 = k/4",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_solve_for_k",
            name: "Step 2: Solve for k",
            type: "steps",
            description: "Solve for the constant k",
            value: "Step 2: k = 8 × 4 = 32",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_write_equation",
            name: "Step 3: Write Equation",
            type: "steps",
            description: "Write the complete equation",
            value: "Step 3: y = 32/x",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step4_substitute_target",
            name: "Step 4: Substitute Target",
            type: "steps",
            description: "Substitute target value",
            value: "Step 4: y = 32/2 = 16",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The value of y when x = 2",
            value: "y = 16",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "variation_equation", to: "step1_substitute_known", type: "used in", strength: 10 },
          { from: "known_values", to: "step1_substitute_known", type: "used in", strength: 9 },
          { from: "step1_substitute_known", to: "step2_solve_for_k", type: "leads to", strength: 10 },
          { from: "step2_solve_for_k", to: "step3_write_equation", type: "leads to", strength: 10 },
          { from: "variation_equation", to: "step3_write_equation", type: "used in", strength: 9 },
          { from: "step3_write_equation", to: "step4_substitute_target", type: "leads to", strength: 10 },
          { from: "target_value", to: "step4_substitute_target", type: "used in", strength: 9 },
          { from: "step4_substitute_target", to: "solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'rates_ratios_inter_05',
      problem: "A mixture contains alcohol and water in the ratio 3:2. If there are 15 liters of alcohol, how much water is there?",
      problemType: "rates_ratios_variations",
      difficulty: "intermediate",
      description: "Ratio problem with given quantity",
      graphData: {
        nodes: [
          {
            id: "given_ratio",
            name: "Given Ratio",
            type: "concepts",
            description: "The ratio of alcohol to water",
            value: "3:2 (alcohol:water)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "alcohol_amount",
            name: "Alcohol Amount",
            type: "concepts",
            description: "The known amount of alcohol",
            value: "15 liters",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "ratio_proportion_formula",
            name: "Ratio Proportion Formula",
            type: "formulas",
            description: "Water amount = (Water ratio × Alcohol amount) ÷ Alcohol ratio",
            value: "Water amount = (Water ratio × Alcohol amount) ÷ Alcohol ratio",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_identify_ratio",
            name: "Step 1: Identify Ratio",
            type: "steps",
            description: "Identify the given ratio parts",
            value: "Step 1: Alcohol ratio = 3, Water ratio = 2",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Apply ratio proportion formula",
            value: "Step 2: Water = (2 × 15) ÷ 3 = 10 liters",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_write_answer",
            name: "Step 3: Write Answer",
            type: "steps",
            description: "Write the final answer",
            value: "Step 3: Write answer = 10 liters",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The amount of water",
            value: "10 liters",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "given_ratio", to: "ratio_proportion_formula", type: "used with", strength: 10 },
          { from: "alcohol_amount", to: "ratio_proportion_formula", type: "used in", strength: 9 },
          { from: "given_ratio", to: "step1_identify_ratio", type: "used in", strength: 9 },
          { from: "alcohol_amount", to: "step1_identify_ratio", type: "used in", strength: 9 },
          { from: "ratio_proportion_formula", to: "step2_apply_formula", type: "used in", strength: 10 },
          { from: "step1_identify_ratio", to: "step2_apply_formula", type: "leads to", strength: 10 },
          { from: "step2_apply_formula", to: "step3_write_answer", type: "leads to", strength: 10 },
          { from: "step3_write_answer", to: "solution", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'rates_ratios_hard_01',
      problem: "Three pipes can fill a tank in 4, 6, and 12 hours respectively. How long will it take if all three pipes are used together?",
      problemType: "rates_ratios_variations",
      difficulty: "harder",
      description: "Multiple combined work rates",
      graphData: {
        nodes: [
          {
            id: "pipe1_rate",
            name: "Pipe 1 Rate",
            type: "concepts",
            description: "First pipe's work rate",
            value: "1/4 of tank per hour",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "pipe2_rate",
            name: "Pipe 2 Rate",
            type: "concepts",
            description: "Second pipe's work rate",
            value: "1/6 of tank per hour",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "pipe3_rate",
            name: "Pipe 3 Rate",
            type: "concepts",
            description: "Third pipe's work rate",
            value: "1/12 of tank per hour",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "combined_work_formula",
            name: "Combined Work Formula",
            type: "formulas",
            description: "Combined rate = Sum of all individual rates",
            value: "Combined Rate = Rate₁ + Rate₂ + Rate₃ + ... + Rateₙ",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step1_identify_rates",
            name: "Step 1: Identify Rates",
            type: "steps",
            description: "Identify all individual work rates",
            value: "Step 1: Pipe 1 = 1/4, Pipe 2 = 1/6, Pipe 3 = 1/12",
            importance: 9,
            x: -100, y: 50, z: 25
          },
          {
            id: "step2_add_rates",
            name: "Step 2: Add Rates",
            type: "steps",
            description: "Add all individual rates",
            value: "Step 2: Combined = 1/4 + 1/6 + 1/12 = 6/12 = 1/2 per hour",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "step3_invert_rate",
            name: "Step 3: Invert Rate",
            type: "steps",
            description: "Invert to find total time",
            value: "Step 3: Total time = 1 ÷ (1/2) = 2 hours",
            importance: 10,
            x: 100, y: 50, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Time to fill tank with all pipes",
            value: "2 hours",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "pipe1_rate", to: "combined_work_formula", type: "used in", strength: 10 },
          { from: "pipe2_rate", to: "combined_work_formula", type: "used in", strength: 10 },
          { from: "pipe3_rate", to: "combined_work_formula", type: "used in", strength: 10 },
          { from: "pipe1_rate", to: "step1_identify_rates", type: "used in", strength: 9 },
          { from: "pipe2_rate", to: "step1_identify_rates", type: "used in", strength: 9 },
          { from: "pipe3_rate", to: "step1_identify_rates", type: "used in", strength: 9 },
          { from: "combined_work_formula", to: "step2_add_rates", type: "used in", strength: 10 },
          { from: "step1_identify_rates", to: "step2_add_rates", type: "leads to", strength: 10 },
          { from: "step2_add_rates", to: "step3_invert_rate", type: "leads to", strength: 10 },
          { from: "step3_invert_rate", to: "solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'rates_ratios_hard_02',
      problem: "If y varies jointly with x and z, and y = 24 when x = 3 and z = 2, find y when x = 4 and z = 5",
      problemType: "rates_ratios_variations",
      difficulty: "harder",
      description: "Joint variation problem",
      graphData: {
        nodes: [
          {
            id: "variation_equation",
            name: "Variation Equation",
            type: "formulas",
            description: "Joint variation formula",
            value: "y = kxz",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "known_values",
            name: "Known Values",
            type: "concepts",
            description: "Given values to find k",
            value: "y = 24 when x = 3 and z = 2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "target_values",
            name: "Target Values",
            type: "concepts",
            description: "The x and z values we want to find y for",
            value: "x = 4 and z = 5",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_substitute_known",
            name: "Step 1: Substitute Known",
            type: "steps",
            description: "Substitute known values to find k",
            value: "Step 1: 24 = k × 3 × 2",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_solve_for_k",
            name: "Step 2: Solve for k",
            type: "steps",
            description: "Solve for the constant k",
            value: "Step 2: k = 24 ÷ (3 × 2) = 4",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_write_equation",
            name: "Step 3: Write Equation",
            type: "steps",
            description: "Write the complete equation",
            value: "Step 3: y = 4xz",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step4_substitute_target",
            name: "Step 4: Substitute Target",
            type: "steps",
            description: "Substitute target values",
            value: "Step 4: y = 4 × 4 × 5 = 80",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The value of y when x = 4 and z = 5",
            value: "y = 80",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "variation_equation", to: "step1_substitute_known", type: "used in", strength: 10 },
          { from: "known_values", to: "step1_substitute_known", type: "used in", strength: 9 },
          { from: "step1_substitute_known", to: "step2_solve_for_k", type: "leads to", strength: 10 },
          { from: "step2_solve_for_k", to: "step3_write_equation", type: "leads to", strength: 10 },
          { from: "variation_equation", to: "step3_write_equation", type: "used in", strength: 9 },
          { from: "step3_write_equation", to: "step4_substitute_target", type: "leads to", strength: 10 },
          { from: "target_values", to: "step4_substitute_target", type: "used in", strength: 9 },
          { from: "step4_substitute_target", to: "solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'rates_ratios_hard_03',
      problem: "A train travels at 60 km/h for the first half of the journey and 80 km/h for the second half. What is the average speed for the entire journey?",
      problemType: "rates_ratios_variations",
      difficulty: "harder",
      description: "Average speed with equal distances",
      graphData: {
        nodes: [
          {
            id: "first_half",
            name: "First Half",
            type: "concepts",
            description: "First half of journey",
            value: "60 km/h for distance d",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "second_half",
            name: "Second Half",
            type: "concepts",
            description: "Second half of journey",
            value: "80 km/h for distance d",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "harmonic_mean_formula",
            name: "Harmonic Mean Formula",
            type: "formulas",
            description: "For equal distances, average speed = harmonic mean",
            value: "Average Speed = 2 × (Speed₁ × Speed₂) ÷ (Speed₁ + Speed₂)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_identify_speeds",
            name: "Step 1: Identify Speeds",
            type: "steps",
            description: "Identify the two speeds",
            value: "Step 1: Speed 1 = 60 km/h, Speed 2 = 80 km/h",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_apply_harmonic_mean",
            name: "Step 2: Apply Harmonic Mean",
            type: "steps",
            description: "Apply harmonic mean formula",
            value: "Step 2: Average = 2 × (60 × 80) ÷ (60 + 80) = 9600 ÷ 140",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_calculate_result",
            name: "Step 3: Calculate Result",
            type: "steps",
            description: "Calculate the final result",
            value: "Step 3: Average speed = 9600 ÷ 140 ≈ 68.6 km/h",
            importance: 10,
            x: 100, y: 0, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The average speed for the entire journey",
            value: "68.6 km/h",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "first_half", to: "harmonic_mean_formula", type: "used in", strength: 10 },
          { from: "second_half", to: "harmonic_mean_formula", type: "used in", strength: 10 },
          { from: "first_half", to: "step1_identify_speeds", type: "used in", strength: 9 },
          { from: "second_half", to: "step1_identify_speeds", type: "used in", strength: 9 },
          { from: "harmonic_mean_formula", to: "step2_apply_harmonic_mean", type: "used in", strength: 10 },
          { from: "step1_identify_speeds", to: "step2_apply_harmonic_mean", type: "leads to", strength: 10 },
          { from: "step2_apply_harmonic_mean", to: "step3_calculate_result", type: "leads to", strength: 10 },
          { from: "step3_calculate_result", to: "solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'rates_ratios_hard_04',
      problem: "A mixture contains three liquids A, B, and C in the ratio 2:3:5. If there are 30 liters of liquid A, how much of liquid C is there?",
      problemType: "rates_ratios_variations",
      difficulty: "harder",
      description: "Three-part ratio problem",
      graphData: {
        nodes: [
          {
            id: "given_ratio",
            name: "Given Ratio",
            type: "concepts",
            description: "The ratio of three liquids",
            value: "2:3:5 (A:B:C)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "liquid_a_amount",
            name: "Liquid A Amount",
            type: "concepts",
            description: "The known amount of liquid A",
            value: "30 liters",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "ratio_proportion_formula",
            name: "Ratio Proportion Formula",
            type: "formulas",
            description: "Liquid C amount = (C ratio × A amount) ÷ A ratio",
            value: "Liquid C amount = (C ratio × A amount) ÷ A ratio",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_identify_ratios",
            name: "Step 1: Identify Ratios",
            type: "steps",
            description: "Identify the ratio parts",
            value: "Step 1: A ratio = 2, C ratio = 5",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Apply ratio proportion formula",
            value: "Step 2: Liquid C = (5 × 30) ÷ 2 = 75 liters",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_write_answer",
            name: "Step 3: Write Answer",
            type: "steps",
            description: "Write the final answer",
            value: "Step 3: Write answer = 75 liters",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The amount of liquid C",
            value: "75 liters",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "given_ratio", to: "ratio_proportion_formula", type: "used with", strength: 10 },
          { from: "liquid_a_amount", to: "ratio_proportion_formula", type: "used in", strength: 9 },
          { from: "given_ratio", to: "step1_identify_ratios", type: "used in", strength: 9 },
          { from: "liquid_a_amount", to: "step1_identify_ratios", type: "used in", strength: 9 },
          { from: "ratio_proportion_formula", to: "step2_apply_formula", type: "used in", strength: 10 },
          { from: "step1_identify_ratios", to: "step2_apply_formula", type: "leads to", strength: 10 },
          { from: "step2_apply_formula", to: "step3_write_answer", type: "leads to", strength: 10 },
          { from: "step3_write_answer", to: "solution", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'rates_ratios_hard_05',
      problem: "If y varies directly with x² and y = 12 when x = 2, find y when x = 5",
      problemType: "rates_ratios_variations",
      difficulty: "harder",
      description: "Direct variation with square",
      graphData: {
        nodes: [
          {
            id: "variation_equation",
            name: "Variation Equation",
            type: "formulas",
            description: "Direct variation with square formula",
            value: "y = kx²",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "known_values",
            name: "Known Values",
            type: "concepts",
            description: "Given values to find k",
            value: "y = 12 when x = 2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "target_value",
            name: "Target Value",
            type: "concepts",
            description: "The x value we want to find y for",
            value: "x = 5",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_substitute_known",
            name: "Step 1: Substitute Known",
            type: "steps",
            description: "Substitute known values to find k",
            value: "Step 1: 12 = k × 2² = k × 4",
            importance: 9,
            x: -100, y: 0, z: 25
          },
          {
            id: "step2_solve_for_k",
            name: "Step 2: Solve for k",
            type: "steps",
            description: "Solve for the constant k",
            value: "Step 2: k = 12 ÷ 4 = 3",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_write_equation",
            name: "Step 3: Write Equation",
            type: "steps",
            description: "Write the complete equation",
            value: "Step 3: y = 3x²",
            importance: 9,
            x: 100, y: 0, z: 25
          },
          {
            id: "step4_substitute_target",
            name: "Step 4: Substitute Target",
            type: "steps",
            description: "Substitute target value",
            value: "Step 4: y = 3 × 5² = 3 × 25 = 75",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "The value of y when x = 5",
            value: "y = 75",
            importance: 10,
            x: 0, y: 100, z: -15
          }
        ],
        links: [
          { from: "variation_equation", to: "step1_substitute_known", type: "used in", strength: 10 },
          { from: "known_values", to: "step1_substitute_known", type: "used in", strength: 9 },
          { from: "step1_substitute_known", to: "step2_solve_for_k", type: "leads to", strength: 10 },
          { from: "step2_solve_for_k", to: "step3_write_equation", type: "leads to", strength: 10 },
          { from: "variation_equation", to: "step3_write_equation", type: "used in", strength: 9 },
          { from: "step3_write_equation", to: "step4_substitute_target", type: "leads to", strength: 10 },
          { from: "target_value", to: "step4_substitute_target", type: "used in", strength: 9 },
          { from: "step4_substitute_target", to: "solution", type: "equals", strength: 10 }
        ]
      }
    }
  ]
}; 